import React, { useEffect, useId, useState } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Send, History, Trash2 } from 'lucide-react';
import Modal from '../common/Modal';
import { summarizeIcReview, formatSigned, formatUkDate, formatUkTime, LARGE_QUANTITY } from './stockTakeLogic';

const STATUS_LABEL = { not_scanned: 'Uncounted', partial: 'Short', over: 'Over', complete: 'Counted' };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Review before submit. IC mode lists every line that differs from the document; when
// most lines (or all) are uncounted the operator must type the document number.
export const ReviewSubmitDialog = ({ open, scanMode, icDocInfo, icLines = [], freeItems = [], isSubmitting, onCancel, onConfirm }) => {
  const confirmId = useId();
  const [typedDocNo, setTypedDocNo] = useState('');
  useEffect(() => {
    if (open) setTypedDocNo('');
  }, [open]);

  if (!open) return null;

  const isIcMode = scanMode === 'icDocument';
  const review = isIcMode ? summarizeIcReview(icLines) : null;
  const docNo = icDocInfo?.docNo || '';
  const docConfirmed = !review?.requiresDocConfirm || typedDocNo.trim().toUpperCase() === docNo.toUpperCase();
  const freeTotal = freeItems.reduce((sum, item) => sum + item.count, 0);
  const largeFree = freeItems.filter((item) => item.count > LARGE_QUANTITY);

  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!isSubmitting}
      title={isIcMode ? `Review ${docNo}` : 'Review stock take'}
      titleIcon={<Send size={20} />}
      size="lg"
      tone={review && (review.uncounted > 0 || review.bigCount > 0) ? 'warning' : 'default'}
      footer={
        <>
          <button type="button" className="st-btn st-btn-secondary" onClick={onCancel} disabled={isSubmitting}>
            Keep counting
          </button>
          <button
            type="button"
            className="st-btn st-btn-primary"
            onClick={onConfirm}
            disabled={isSubmitting || !docConfirmed}
          >
            <Send size={18} aria-hidden="true" /> {isSubmitting ? 'Submitting...' : 'Submit to Priority'}
          </button>
        </>
      }
    >
      {isIcMode ? (
        <>
          <div className="st-review-stats">
            <div className="st-review-stat"><span>{review.lineCount}</span>Lines</div>
            <div className="st-review-stat is-ok"><span>{review.matched}</span>Match</div>
            <div className="st-review-stat is-short"><span>{review.short}</span>Short</div>
            <div className="st-review-stat is-over"><span>{review.over}</span>Over</div>
            <div className="st-review-stat is-missing"><span>{review.uncounted}</span>Uncounted</div>
          </div>
          <p className="st-review-units">
            Units: counted <strong>{review.totalCounted}</strong> of {review.totalExpected} expected
            (net <strong>{formatSigned(review.netDiff)}</strong>)
          </p>

          {review.uncounted > 0 && (
            <div className="st-callout st-callout-danger">
              <AlertTriangle size={20} aria-hidden="true" />
              <p>
                <strong>{plural(review.uncounted, 'uncounted line')}</strong> will be set to <strong>0 stock</strong> in
                Priority. Only submit if those items really are not there.
              </p>
            </div>
          )}
          {review.bigCount > 0 && (
            <div className="st-callout st-callout-warning">
              <AlertTriangle size={20} aria-hidden="true" />
              <p>
                {plural(review.bigCount, 'line')} {review.bigCount === 1 ? 'has' : 'have'} a big variance
                (10 or more units, or 50% or more of the expected quantity). Recount if unsure.
              </p>
            </div>
          )}

          {review.variances.length > 0 ? (
            <div className="st-review-list">
              <table className="st-review-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Item</th>
                    <th className="st-num-col">Exp</th>
                    <th className="st-num-col">Counted</th>
                    <th className="st-num-col">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {review.variances.map((line) => (
                    <tr key={`${line.kline ?? ''}-${line.barcode}`} className={`ic-row-${line.status}`}>
                      <td><span className={`ic-status ${line.status}`}>{STATUS_LABEL[line.status]}</span></td>
                      <td>
                        <span className="st-review-item">{line.description || line.partName || line.barcode}</span>
                        <span className="st-review-barcode">{line.barcode}</span>
                      </td>
                      <td className="st-num-col">{line.expectedCount}</td>
                      <td className="st-num-col">{line.scannedCount}</td>
                      <td className={`st-num-col diff-cell ${line.diff < 0 ? 'negative' : 'positive'}`}>
                        {formatSigned(line.diff)}
                        {line.bigVariance && (
                          <span className="st-big-flag"><AlertTriangle size={14} aria-hidden="true" /> Big</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="st-review-allmatch">
              <CheckCircle size={20} aria-hidden="true" /> Every line matches the document.
            </p>
          )}

          {review.requiresDocConfirm && (
            <div className="st-doc-confirm">
              <label htmlFor={confirmId}>
                {review.uncounted === review.lineCount
                  ? 'Nothing has been counted. '
                  : `More than half the lines are uncounted. `}
                Type <strong>{docNo}</strong> to confirm.
              </label>
              <input
                id={confirmId}
                type="text"
                className="st-text-input"
                value={typedDocNo}
                onChange={(e) => setTypedDocNo(e.target.value.toUpperCase())}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <p className="st-review-units">
            <strong>{plural(freeItems.length, 'barcode')}</strong>, <strong>{plural(freeTotal, 'item')}</strong> in total.
            Priority stock for each barcode will be set to the counted quantity.
          </p>
          {largeFree.length > 0 && (
            <div className="st-callout st-callout-warning">
              <AlertTriangle size={20} aria-hidden="true" />
              <p>{plural(largeFree.length, 'barcode')} counted more than {LARGE_QUANTITY}. Check {largeFree.length === 1 ? 'it' : 'them'} before submitting.</p>
            </div>
          )}
          <div className="st-review-list">
            <table className="st-review-table">
              <thead>
                <tr>
                  <th>Barcode</th>
                  <th className="st-num-col">Count</th>
                </tr>
              </thead>
              <tbody>
                {freeItems.map((item) => (
                  <tr key={item.barcode}>
                    <td className="st-review-barcode-cell">{item.barcode}</td>
                    <td className="st-num-col">
                      {item.count}
                      {item.count > LARGE_QUANTITY && (
                        <span className="st-big-flag"><AlertTriangle size={14} aria-hidden="true" /> Large</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="st-review-final">Submitting adjusts stock in Priority and cannot be undone here.</p>
    </Modal>
  );
};

export const SuccessModal = ({ show, submissionResult, onClose }) => {
  if (!show || !submissionResult) return null;
  const skipped = submissionResult.skippedBarcodes || [];

  return (
    <Modal
      open
      onClose={onClose}
      title="Stock take submitted"
      titleIcon={<CheckCircle size={22} />}
      size="sm"
      tone={skipped.length > 0 || submissionResult.warning ? 'warning' : 'success'}
      footer={
        <button type="button" className="st-btn st-btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <p className="st-modal-lead">Your stock take has been sent to Priority ERP.</p>
      {submissionResult.documentNumber && submissionResult.documentNumber !== 'N/A' && (
        <div className="st-docno">
          <span className="st-docno-label">Document number</span>
          <span className="st-docno-value">{submissionResult.documentNumber}</span>
        </div>
      )}
      <ul className="st-result-list">
        <li>{plural(submissionResult.uniqueItems, 'line')} submitted</li>
        <li>{plural(submissionResult.totalCount, 'item')} counted in total</li>
      </ul>
      {submissionResult.warning && (
        <div className="st-callout st-callout-warning">
          <AlertTriangle size={20} aria-hidden="true" />
          <p>{submissionResult.warning}</p>
        </div>
      )}
      {skipped.length > 0 && (
        <div className="st-callout st-callout-warning">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <p>
              <strong>{plural(skipped.length, 'barcode')} {skipped.length === 1 ? 'was' : 'were'} NOT submitted.</strong>{' '}
              Check {skipped.length === 1 ? 'it' : 'them'} in Priority and count again if needed:
            </p>
            <ul className="st-skipped-list">
              {skipped.map((s) => (
                <li key={s.barcode}>
                  <span className="st-mono">{s.barcode}</span>
                  {s.reason && <span className="st-skipped-reason"> &ndash; {s.reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Modal>
  );
};

export const ErrorModal = ({ show, errorDetails, onClose }) => {
  if (!show || !errorDetails) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={errorDetails.isNetworkError ? 'Connection error'
        : errorDetails.changed?.length ? 'Recount needed - nothing was posted'
        : 'Stock take rejected by Priority'}
      titleIcon={<AlertCircle size={22} />}
      size="md"
      tone="danger"
      footer={
        <button type="button" className="st-btn st-btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="st-error-message">{errorDetails.message}</p>
      <p className="st-modal-note">Nothing was cleared. Your counts are still here, so you can fix the problem and submit again.</p>

      {errorDetails.priorityStatus && (
        <p className="st-modal-note st-mono">Priority API status: {errorDetails.priorityStatus}</p>
      )}

      {errorDetails.changed && errorDetails.changed.length > 0 && (
        <div className="st-callout st-callout-warning">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <p><strong>Priority changed for these parts while you were counting.</strong> The new &quot;expected&quot; figures are on screen now: recount them on the shelf, correct the count, then submit again.</p>
            <ul className="st-skipped-list">
              {errorDetails.changed.map((c) => (
                <li key={c.partName}>
                  <span className="st-mono">{c.partName}</span>
                  <span className="st-skipped-reason"> &ndash; expected was {c.was}, now {c.now}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {errorDetails.notFoundBarcodes && errorDetails.notFoundBarcodes.length > 0 && (
        <div className="st-callout st-callout-warning">
          <AlertTriangle size={20} aria-hidden="true" />
          <p>
            Not found in Priority:{' '}
            <span className="st-mono">{errorDetails.notFoundBarcodes.map((i) => i.barcode).join(', ')}</span>
          </p>
        </div>
      )}

      <details className="st-details">
        <summary>What was submitted ({plural(errorDetails.submittedItems.length, 'line')})</summary>
        <ul className="st-submitted-list">
          {errorDetails.submittedItems.map((item, index) => (
            <li key={index}>
              <span className="st-mono">{item.barcode}</span>
              <span>&times; {item.count}</span>
            </li>
          ))}
        </ul>
      </details>
    </Modal>
  );
};

// Danger confirmation for Clear / Close document
export const DangerConfirmDialog = ({ action, onCancel, onConfirm }) => {
  if (!action) return null;
  return (
    <Modal
      open
      onClose={onCancel}
      title={action.title}
      titleIcon={<Trash2 size={20} />}
      size="sm"
      tone="danger"
      footer={
        <>
          <button type="button" className="st-btn st-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="st-btn st-btn-danger" onClick={onConfirm}>
            {action.confirmLabel}
          </button>
        </>
      }
    >
      <p className="st-modal-lead">{action.message}</p>
      <p className="st-modal-note">This cannot be undone.</p>
    </Modal>
  );
};

// An IC document already has a count in progress: resume it or start again
export const ResumeSessionDialog = ({ prompt, onResume, onStartNew }) => {
  if (!prompt) return null;
  const { docNo, lineCount, countedLines, updatedAt } = prompt;
  return (
    <Modal
      open
      dismissible={false}
      title="Count already in progress"
      titleIcon={<History size={20} />}
      size="sm"
      tone="warning"
      footer={
        <>
          <button type="button" className="st-btn st-btn-secondary" onClick={onStartNew}>
            Start new
          </button>
          <button type="button" className="st-btn st-btn-primary" onClick={onResume}>
            Resume count
          </button>
        </>
      }
    >
      <dl className="st-resume-card">
        <div>
          <dt>Document</dt>
          <dd className="st-mono">{docNo}</dd>
        </div>
        <div>
          <dt>Lines counted</dt>
          <dd>{countedLines !== null ? `${countedLines} of ${lineCount}` : `? of ${lineCount}`}</dd>
        </div>
        <div>
          <dt>Last saved</dt>
          <dd>{updatedAt ? `${formatUkDate(updatedAt)} ${formatUkTime(updatedAt)}` : 'Unknown'}</dd>
        </div>
      </dl>
      <p className="st-modal-note">
        <strong>Resume count</strong> carries on with those counts. <strong>Start new</strong> begins again from zero;
        the earlier count is left as it is.
      </p>
    </Modal>
  );
};
